import { deriveParentNumber } from "./csi-hierarchy";
import type { CsiSystem, DsmScopeItem, TemplateItem } from "./types";

/** One ancestor (or the leaf) in a catalog path, root first. */
export interface ClassificationPathNode {
  number: string;
  title: string;
}

/**
 * One code an item is mapped to. `title` is a display snapshot so rows still
 * render if the catalog later changes. `termId` is the GraphQL catalog PK.
 * `path` is the `parent_id` chain (root first), stored so the tree does not
 * re-derive parents from the code string.
 */
export interface Classification {
  system: CsiSystem | "CUSTOM";
  code: string; // "" for CUSTOM
  title: string;
  termId?: string;
  parentId?: string | null;
  path?: ClassificationPathNode[];
}

/**
 * What an item is classified as: a code in either standard — both at once is
 * fine — or, only when it is uncoded, a bare label. At least one of the three
 * is required for an item to be saved.
 */
export interface Classifications {
  uf?: Classification;
  mf?: Classification;
  label?: Classification;
}

/** The pre-v4 shapes: a v1 ordered ladder, or a v2 set. */
export type LegacyLevel = Classification;

/**
 * The code's ancestor chain, root first, ending in the code itself. This is
 * the item's position in a tree — derived on demand, never stored, so a CSI
 * data refresh improves existing items for free.
 */
export function ancestorCodes(code: string, system: CsiSystem): string[] {
  const start = code.trim();
  if (!start) return [];
  const chain: string[] = [start];
  let cur = deriveParentNumber(start, system);
  while (cur) {
    chain.unshift(cur);
    cur = deriveParentNumber(cur, system);
  }
  return chain;
}

/** The item's code in one standard, if it holds one. */
export function codeOf(
  c: Classifications | undefined,
  system: CsiSystem
): Classification | undefined {
  return system === "UF" ? c?.uf : c?.mf;
}

/** Every filled slot, UniFormat first — what renderers walk. */
export function entriesOf(c: Classifications | undefined): Classification[] {
  return [c?.uf, c?.mf, c?.label].filter(Boolean) as Classification[];
}

/** Whether the item carries any classification at all. */
export function isClassified(c: Classifications | undefined): boolean {
  return entriesOf(c).length > 0;
}

/**
 * Root grouping key for the division filter, e.g. "B" or "07 00 00". Empty
 * when the item holds no code in the system being asked about — an
 * MF-only item has no UniFormat division to sit under. Prefers the stored
 * `parent_id` path; does not infer parents from the code string.
 */
export function topOf(
  c: Classifications | undefined,
  system: CsiSystem
): string {
  const hit = codeOf(c, system);
  if (!hit) return "";
  return hit.path?.[0]?.number ?? hit.code;
}

/** Drop a blank entry to `undefined` and trim what survives. */
export function normalizeClassification(
  c: Classification | undefined
): Classification | undefined {
  if (!c) return undefined;
  const code = c.code.trim();
  const title = c.title.trim();
  if (c.system === "CUSTOM") {
    return title
      ? {
          system: "CUSTOM",
          code: "",
          title,
          ...(c.termId ? { termId: c.termId } : {})
        }
      : undefined;
  }
  if (!code) return undefined;
  return {
    system: c.system,
    code,
    title,
    ...(c.termId ? { termId: c.termId } : {}),
    ...(c.parentId !== undefined ? { parentId: c.parentId } : {}),
    ...(c.path?.length ? { path: c.path } : {})
  };
}

/** Keep an entry only when it actually belongs in the slot it arrived in. */
function inSlot(
  c: Classification | undefined,
  system: Classification["system"]
): Classification | undefined {
  const hit = normalizeClassification(c);
  return hit?.system === system ? hit : undefined;
}

/**
 * Enforce the shape's one rule: a label is the uncoded fallback, so it drops
 * away the moment the item carries a code in either standard.
 */
export function normalizeClassifications(
  c: Classifications | undefined
): Classifications | undefined {
  if (!c) return undefined;
  const uf = inSlot(c.uf, "UF");
  const mf = inSlot(c.mf, "MF");
  const label = uf || mf ? undefined : inSlot(c.label, "CUSTOM");
  if (!uf && !mf && !label) return undefined;
  return {
    ...(uf ? { uf } : {}),
    ...(mf ? { mf } : {}),
    ...(label ? { label } : {})
  };
}

/**
 * Collapse a stored v1 ladder or v2 set into the v4 shape, keeping the most
 * specific code in EACH standard — ladders run root → leaf, so the last coded
 * level in a system is the specific one. A custom label survives only when
 * neither standard is coded.
 */
export function foldToPair(
  levels: LegacyLevel[] | undefined
): Classifications | undefined {
  if (!levels?.length) return undefined;
  const lastCoded = (system: CsiSystem) =>
    [...levels].reverse().find((l) => l.system === system && l.code.trim());
  const pathOf = (system: CsiSystem): ClassificationPathNode[] | undefined => {
    const nodes = levels
      .filter((l) => l.system === system && l.code.trim())
      .map((l) => ({ number: l.code.trim(), title: l.title.trim() }));
    return nodes.length ? nodes : undefined;
  };
  const withPath = (system: CsiSystem) => {
    const hit = lastCoded(system);
    if (!hit) return undefined;
    const path = hit.path?.length ? hit.path : pathOf(system);
    return path ? { ...hit, path } : hit;
  };
  return normalizeClassifications({
    uf: withPath("UF"),
    mf: withPath("MF"),
    label: levels.find((l) => l.system === "CUSTOM" && l.title.trim())
  });
}

/**
 * Identity of an item's classification, for template dedup. Titles are
 * excluded so a drifted CSI title never re-seeds as a duplicate. An
 * unclassified item would otherwise key to the empty string and collide with
 * every other one, so it keys off its description instead.
 */
export function classificationKey(
  c: Classifications | undefined,
  fallback: string
): string {
  const coded = [c?.uf, c?.mf]
    .filter(Boolean)
    .map((x) => `${x!.system}:${x!.code}`);
  if (coded.length) return coded.join("|");
  if (c?.label) return `CUSTOM:${c.label.title.toLowerCase()}`;
  return `desc:${fallback.trim().toLowerCase()}`;
}

/** Read the classification off any stored shape, v1 through v4. */
function readClassification(raw: {
  hierarchy?: LegacyLevel[];
  classifications?: LegacyLevel[];
  classification?: Classification | Classifications;
}): Classifications | undefined {
  const stored = raw.classification;
  if (stored) {
    // v3 wrote one entry under this key; v4 writes the slotted container.
    return "system" in stored
      ? foldToPair([stored as Classification])
      : normalizeClassifications(stored as Classifications);
  }
  if (Array.isArray(raw.classifications))
    return foldToPair(raw.classifications);
  return foldToPair(raw.hierarchy);
}

/**
 * Normalize one stored scope item to the v4 shape. Reads run every item
 * through this, so a doc written before the migration still loads.
 */
export function migrateScopeItem(raw: unknown): DsmScopeItem {
  const { hierarchy, classifications, classification, ...rest } = (raw ??
    {}) as Record<string, unknown> & {
    hierarchy?: LegacyLevel[];
    top?: string;
    classifications?: LegacyLevel[];
    classification?: Classification | Classifications;
  };
  // `top` was derived from `hierarchy` and is gone since v2 — drop it rather
  // than bind an unused variable.
  delete (rest as { top?: unknown }).top;
  const folded = readClassification({
    hierarchy,
    classifications,
    classification
  });
  return {
    ...(rest as Omit<DsmScopeItem, "classification">),
    ...(folded ? { classification: folded } : {})
  };
}

/** The template-item equivalent: classification + defaults only. */
export function migrateTemplateItem(raw: unknown): TemplateItem {
  const { hierarchy, classifications, classification, ...rest } = (raw ??
    {}) as Record<string, unknown> & {
    hierarchy?: LegacyLevel[];
    classifications?: LegacyLevel[];
    classification?: Classification | Classifications;
  };
  const folded = readClassification({
    hierarchy,
    classifications,
    classification
  });
  return {
    ...(rest as Omit<TemplateItem, "classification">),
    ...(folded ? { classification: folded } : {})
  };
}
